import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, BookOpen, Banknote, Users, Boxes, UserCog, LogOut, ChevronDown } from "lucide-react";
import { BrandLogo } from "./BrandLogo";
import { useAuth } from "../lib/auth";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "./ui/dropdown-menu";

const links = [
  { to: "/ceo", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/ceo/ledger", label: "Ledger", icon: BookOpen },
  { to: "/ceo/payroll", label: "Payroll", icon: Banknote },
  { to: "/ceo/products", label: "Products", icon: Boxes },
  { to: "/ceo/staff", label: "Staff", icon: Users },
  { to: "/ceo/users", label: "Managers", icon: UserCog },
];

export function CeoNav() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const initials = (user?.name || "CO").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <header className="sticky top-0 z-30 bg-white/85 backdrop-blur border-b border-border">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between gap-4">
        <BrandLogo subtitle="Operations Dashboard" />
        <nav className="hidden md:flex items-center gap-1">
          {links.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`ceo-nav-${label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-3 py-2 rounded-lg text-sm flex items-center gap-2 transition-colors ${
                  isActive ? "bg-primary/10 text-primary font-semibold" : "text-foreground hover:bg-secondary"
                }`
              }
            >
              <Icon size={16} />
              {label}
            </NavLink>
          ))}
        </nav>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="ceo-user-menu"
              className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-secondary transition-colors"
            >
              <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center text-xs font-semibold">
                {initials}
              </div>
              <div className="hidden sm:block text-sm text-left leading-tight">
                <div className="font-semibold">{user?.name}</div>
                <div className="text-xs text-muted-foreground">CEO Admin</div>
              </div>
              <ChevronDown size={16} className="text-muted-foreground" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => nav("/ceo/users")} data-testid="menu-managers">
              <UserCog size={14} className="mr-2" /> Manage users
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={async () => { await logout(); nav("/login"); }}
              data-testid="menu-logout"
            >
              <LogOut size={14} className="mr-2" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {/* mobile nav */}
      <div className="md:hidden px-4 pb-2 overflow-x-auto">
        <div className="flex gap-2">
          {links.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `whitespace-nowrap px-3 py-1.5 rounded-full text-xs flex items-center gap-1.5 border ${
                  isActive ? "bg-primary text-primary-foreground border-primary" : "bg-white border-border text-foreground"
                }`
              }
            >
              <Icon size={12} /> {label}
            </NavLink>
          ))}
        </div>
      </div>
    </header>
  );
}
